import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { updateSelfSignedFlag } from "redux/appSlice";
import urls from "global/constants/UrlConstants";
import strings from "global/constants/StringConstants";
import { getAppPlanLimitByEntity } from "../../SignRequestService";
import { handleErrorMessage, isUnlimitedLimit } from "helpers/methods";
import UnlimitedLabel from "global/components/UnlimitedLabel/UnlimitedLabel";
import { pressableProps } from "../mobileA11y";

export type SigningModeId = "certified" | "basic";

interface SigningModeOption {
  id: SigningModeId;
  title: string;
  /** Remaining quota for this mode (sourced from the account plan). */
  remaining: number;
  description: string;
  badgeLabel: string;
  badgeVariant: "high" | "basic";
  disabled?: boolean;
}

const CERTIFIED_DESCRIPTION =
  "A secure electronic signature with audit trail plus a digital certificate that verifies the signer’s identity. Ideal for legal contracts, financial agreements, real estate deals, and any important document needing authenticity and compliance.";
const BASIC_DESCRIPTION =
  "A simple electronic signature with audit trail (like timestamps and signer details), no digital certificate. Best for internal approvals, HR forms, NDAs, and any low-risk agreements where speed is important.";

interface MobileModeProps {
  /**
   * Self-sign flow: the mode cards pick Basic vs Certified eSign and drive the
   * redux `selfSigned` flag (Basic → selfSigned=true, Certified → false),
   * mirroring the desktop self-sign "Select eSign Mode" cards.
   */
  selfSign?: boolean;
  basicRemaining?: number;
  certifiedRemaining?: number;
  certifiedDisabled?: boolean;
}

const MobileMode: React.FC<MobileModeProps> = ({
  selfSign,
  basicRemaining = 0,
  certifiedRemaining = 0,
  certifiedDisabled = false,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const dispatch = useAppDispatch();
  const signRequest = useAppSelector((state: RootState) => state.app.signRequest);

  // Generic (me-and-others) flow: quota + certified gating are fetched here,
  // mirroring desktop RequestConfiguration. The self-sign flow receives these
  // via props from SelfSignRequestComponent.
  const [genBasicRemaining, setGenBasicRemaining] = useState(0);
  const [genCertifiedRemaining, setGenCertifiedRemaining] = useState(0);
  const [genCertifiedDisabled, setGenCertifiedDisabled] = useState(false);

  // On prod the eSign mode is not selectable — every request is Certified
  // eSign automatically. On uat/dev we surface the Basic vs Certified choice
  // (same as desktop).
  const isProdAuto = !selfSign && urls.PROD;

  useEffect(() => {
    if (selfSign) return;
    let cancelled = false;
    (async () => {
      try {
        const [selfSignedLimit, notarius] = await Promise.all([
          getAppPlanLimitByEntity(strings.selfSignedEnveloped),
          getAppPlanLimitByEntity(strings.notariusVerifiedEnvelope),
        ]);
        if (cancelled) return;

        const basicRem =
          (selfSignedLimit?.limit ?? 0) - (selfSignedLimit?.current ?? 0);
        const certRem = (notarius?.limit ?? 0) - (notarius?.current ?? 0);
        const notariusPresent =
          !!notarius &&
          notarius.hasOwnProperty("entity") &&
          notarius.hasOwnProperty("limit");
        const certDisabled =
          !(notariusPresent && (notarius.limit ?? 0) > 0) || certRem <= 0;

        setGenBasicRemaining(basicRem);
        setGenCertifiedRemaining(certRem);
        setGenCertifiedDisabled(certDisabled);

        if (urls.PROD) {
          // Prod: force Certified eSign, no selection.
          if (signRequest.selfSigned) dispatch(updateSelfSignedFlag(false));
        } else if (certDisabled && !signRequest.selfSigned) {
          // Uat: certified unavailable → fall back to Basic.
          dispatch(updateSelfSignedFlag(true));
        }
      } catch (error: any) {
        handleErrorMessage(error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfSign]);

  const basicRem = selfSign ? basicRemaining : genBasicRemaining;
  const certRem = selfSign ? certifiedRemaining : genCertifiedRemaining;
  const certDisabled = selfSign ? certifiedDisabled : genCertifiedDisabled;

  // Basic → selfSigned=true, Certified → selfSigned=false. On prod it's always
  // Certified regardless of the (forced) flag.
  const selectedMode: SigningModeId = isProdAuto
    ? "certified"
    : signRequest.selfSigned
      ? "basic"
      : "certified";

  const certifiedOption: SigningModeOption = {
    id: "certified",
    title: "Certified eSign",
    remaining: certRem,
    description: CERTIFIED_DESCRIPTION,
    badgeLabel: "High security",
    badgeVariant: "high",
    disabled: certDisabled,
  };
  const basicOption: SigningModeOption = {
    id: "basic",
    title: "Basic eSign",
    remaining: basicRem,
    description: BASIC_DESCRIPTION,
    badgeLabel: "Basic Security",
    badgeVariant: "basic",
  };

  // Prod: single, non-selectable Certified card. Otherwise both choices.
  const modes: SigningModeOption[] = isProdAuto
    ? [certifiedOption]
    : [certifiedOption, basicOption];

  const handleSelect = (option: SigningModeOption) => {
    if (isProdAuto || option.disabled) return;
    dispatch(updateSelfSignedFlag(option.id === "basic"));
  };

  return (
    <Box
      role={isProdAuto ? undefined : "radiogroup"}
      aria-label={isProdAuto ? undefined : "Signing mode"}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        px: 2,
        pt: 2,
      }}
    >
      {modes.map((option) => {
        const isSelected = option.id === selectedMode;
        const isHigh = option.badgeVariant === "high";

        return (
          <Box
            key={option.id}
            {...(isProdAuto
              ? {}
              : pressableProps(() => handleSelect(option), {
                  role: "radio",
                  selected: isSelected,
                  selectedAttr: "aria-checked",
                  disabled: option.disabled,
                  label: `${option.title}, ${option.badgeLabel}`,
                }))}
            aria-describedby={`mobile-mode-desc-${option.id}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              p: 2,
              borderRadius: "16px",
              border: `1px solid ${
                isSelected ? m.ePurple[500] : m.grey[50]
              }`,
              bgcolor: m.surface.white,
              cursor:
                isProdAuto || option.disabled ? "default" : "pointer",
              opacity: option.disabled ? 0.6 : 1,
            }}
          >
            {/* Title row + remaining quota */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 1,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
              >
                {/* Radio — hidden when the mode is auto-selected on prod */}
                {!isProdAuto && (
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border: `2px solid ${
                        isSelected ? m.ePurple[500] : m.grey[700]
                      }`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {isSelected && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: m.ePurple[500],
                        }}
                      />
                    )}
                  </Box>
                )}
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    lineHeight: "21px",
                    color: m.base[900],
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {option.title}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: m.base[900],
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {isUnlimitedLimit(option.remaining) ? (
                  <Box component="span" sx={{ color: m.green[500] }}>
                    <UnlimitedLabel iconSize={14} />
                  </Box>
                ) : (
                  <>
                    <Box
                      component="span"
                      sx={{
                        color: option.remaining > 0 ? m.green[500] : m.base[900],
                      }}
                    >
                      {option.remaining}
                    </Box>{" "}
                    remaining
                  </>
                )}
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              id={`mobile-mode-desc-${option.id}`}
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: m.grey[400],
                width: "100%",
              }}
            >
              {option.description}
            </Typography>

            {/* Security badge */}
            <Box
              sx={{
                alignSelf: "flex-start",
                px: 1.5,
                py: 0.75,
                borderRadius: "12px",
                bgcolor: isHigh ? m.red[50] : m.blue[50],
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textAlign: "center",
                  color: isHigh ? m.red[500] : m.blue[500],
                }}
              >
                {option.badgeLabel}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default MobileMode;
